import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, type Team } from '../api';
import { AppShell } from '../components/AppShell';

export function TeamsPage() {
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  async function load() {
    try {
      setErr(null);
      const data = await api.listTeams();
      setTeams(data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Teams</h1>
            <p className="mt-1 text-muted">
              Roster home — teams carry their own colors, logo, and player
              list. A game is two teams + a venue + a sport.
            </p>
          </div>
          {!showForm && (
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              + Add team
            </button>
          )}
        </div>

        {showForm && (
          <AddTeamForm
            onDone={async () => {
              setShowForm(false);
              await load();
            }}
            onCancel={() => setShowForm(false)}
          />
        )}

        {err && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {err}
          </div>
        )}

        {!teams && !err && <div className="text-muted">Loading…</div>}

        {teams && teams.length === 0 && !showForm && (
          <div className="card text-center">
            <div className="mx-auto mb-3 text-4xl">🏆</div>
            <h3 className="text-lg font-semibold">No teams yet</h3>
            <p className="mt-1 text-sm text-muted">
              Add the home team, the visitors, or every team in your league.
            </p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => setShowForm(true)}
            >
              + Add your first team
            </button>
          </div>
        )}

        {teams && teams.length > 0 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {teams.map((t) => (
              <TeamCard key={t.id} team={t} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function TeamCard({ team }: { team: Team }) {
  return (
    <Link
      to={`/teams/${team.id}`}
      className="card transition hover:border-brand/40 hover:bg-card/80"
    >
      <div className="flex items-center gap-3">
        <ColorSwatch
          primary={team.colorPrimary}
          secondary={team.colorSecondary}
        />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-lg font-semibold">{team.name}</h3>
          {team.abbreviation && (
            <div className="font-mono text-xs uppercase tracking-wider text-muted">
              {team.abbreviation}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 text-sm text-muted">
        {team._count?.players ?? 0} player
        {(team._count?.players ?? 0) === 1 ? '' : 's'}
      </div>
    </Link>
  );
}

function ColorSwatch({
  primary,
  secondary,
}: {
  primary: string | null;
  secondary: string | null;
}) {
  const a = primary ?? '#3b82f6';
  const b = secondary ?? a;
  return (
    <div
      className="h-10 w-10 shrink-0 rounded-lg border border-border"
      style={{
        background: `linear-gradient(135deg, ${a} 0%, ${a} 50%, ${b} 50%, ${b} 100%)`,
      }}
    />
  );
}

function AddTeamForm({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [abbreviation, setAbbreviation] = useState('');
  const [colorPrimary, setColorPrimary] = useState('#3b82f6');
  const [colorSecondary, setColorSecondary] = useState('#ffffff');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.createTeam({
        name,
        abbreviation: abbreviation || undefined,
        colorPrimary: colorPrimary || undefined,
        colorSecondary: colorSecondary || undefined,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'failed_to_create');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card space-y-4">
      <h2 className="text-lg font-semibold">New team</h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="label">Name</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Springfield Tigers"
            required
            minLength={2}
          />
        </div>
        <div>
          <label className="label">Abbreviation</label>
          <input
            className="input font-mono uppercase"
            value={abbreviation}
            onChange={(e) =>
              setAbbreviation(e.target.value.toUpperCase().slice(0, 10))
            }
            placeholder="SPR"
            maxLength={10}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Primary</label>
            <input
              type="color"
              className="h-11 w-full cursor-pointer rounded-lg border border-border bg-surface"
              value={colorPrimary}
              onChange={(e) => setColorPrimary(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Secondary</label>
            <input
              type="color"
              className="h-11 w-full cursor-pointer rounded-lg border border-border bg-surface"
              value={colorSecondary}
              onChange={(e) => setColorSecondary(e.target.value)}
            />
          </div>
        </div>
      </div>
      {err && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {err}
        </div>
      )}
      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Create team'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
